import Vue from 'vue';
import VueRouter from 'vue-router';
import { Auth } from '@aws-amplify/auth';
import authHelpers from '../../plugins/auth-helpers';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    redirect: { name: 'User' }
  },
  {
    path: '/:locale?/pay/:qrCode/:currency/:amount?',
    name: 'Pay',
    props: true,
    component: () => import('../../views/Pay/Pay.vue')
  },
  {
    path: '/:locale?/multiple-pay/:qrCodes/:currency/:amount?',
    name: 'MultiplePay',
    component: () => import('../../views/Pay/MultiplePay.vue')
  },
  {
    path: '/pay-card/:qrCode/:currency/:totalAmount',
    name: 'PayCard',
    component: () => import('../../views/Pay/PayCard.vue')
  },
  {
    path: '/pay-confirm/:qrCodes/:currency/:amount/:txId?',
    name: 'ConfirmPayment',
    component: () => import('../../views/Pay/ConfirmPayment')
  },
  {
    path: '/select-employee/:qrCode/:currency',
    name: 'SelectEmployee',
    component: () => import('../../views/Pay/SelectEmployee.vue')
  },
  {
    path: '/select-team/:groupQrCode/:currency',
    name: 'SelectTeamPay',
    component: () => import('../../views/Pay/SelectTeam.vue'),
  },
  {
    path: '/receipt/:transactionId?',
    name: 'Receipt',
    component: () => import('../../views/Receipt/Receipt')
  },
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('../../views/Auth/SignIn')
  },
  {
    path: '/sign-up/:organization?',
    name: 'SignUp',
    component: () => import('../../views/Auth/SignUp')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../../views/Auth/ResetPassword')
  },
  {
    path: '/user/edit/:userId',
    name: 'EditUser',
    component: () => import('../../views/User/EditUser'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user/change-password',
    name: 'ChangePassword',
    component: () => import('../../views/User/ChangePassword'),
    meta: { requiresAuth: true }
  },
  {
    path: '/user',
    name: 'User',
    component: () => import('../../views/User/Dashboard'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organizations/:page?',
    name: 'Organizations',
    component: () => import('../../views/Organizations/Organizations'),
    meta: { requiresBusiness: true }
  },
  {
    path: '/organization/add',
    name: 'CreateOrganization',
    component: () => import('../../views/Organizations/CreateOrganization'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/organization/:id',
    name: 'Organization',
    component: () => import('../../views/Organizations/Organization'),
    meta: { requiresBusiness: true }
  },
  {
    path: '/organization/:id/edit',
    name: 'EditOrganization',
    component: () => import('../../views/Organizations/EditOrganization'),
    meta: { requiresBusiness: true }
  },
  {
    path: '/organization/:id/teams/add',
    name: 'CreateTeam',
    component: () => import('../../views/Teams/CreateTeam'),
    meta: { requiresBusiness: true }
  },
  {
    path: '/organization/:id/teams/:teamId',
    name: 'Team',
    component: () => import('../../views/Teams/Team'),
    meta: { requiresBusiness: true }
  },
  {
    path: '/organization/:id/teams/:teamId/edit',
    name: 'EditTeam',
    component: () => import('../../views/Teams/EditTeam'),
    meta: { requiresBusiness: true }
  },
  {
    path: '/report/:type',
    name: 'HtmlReport',
    component: () => import('../../views/Reports/Html'),
    meta: { requiresAuth: true }
  },
  {
    path: '/customer-terms',
    name: 'CustomerTerms',
    component: () => import('../../views/Terms/CustomerTerms')
  },
  {
    path: '/user-terms',
    name: 'UserTerms',
    component: () => import('../../views/Terms/UserTerms')
  },
  {
    path: '/re-conciliation',
    component: () => import('../../views/Reconciliation/Reconciliation'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'ReConciliation',
        beforeEnter: (to, from, next) => {
          return next({ name: 'SelectTeam' });
        }
      },
      {
        path: 'select-team',
        name: 'SelectTeam',
        component: () => import('../../views/Reconciliation/Steps/SelectTeam')
      },
      {
        path: 'set-date/:teamId',
        name: 'SetDate',
        component: () => import('../../views/Reconciliation/Steps/SetDate')
      },
      {
        path: 'set-amount/:teamId/:startDate?/:endDate?',
        name: 'SetAmount',
        component: () => import('../../views/Reconciliation/Steps/SetAmount')
      },
      {
        path: 'thank-you',
        name: 'ReConciliationThankYou',
        component: () => import('../../views/Reconciliation/Steps/ThankYou')
      }
    ]
  },
  {
    path: '/on-boarding/users/:page?',
    name: 'OnBoardingUsers',
    component: () => import('../../views/OnBoarding/Users'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/upload-passport',
    name: 'UploadPassport',
    component: () => import('../../views/User/UploadPassport'),
    meta: { requiresAuth: true }
  },
  {
    path: '*',
    name: '404',
    component: () => import('../../views/ErrorPage')
  }
];

const router = new VueRouter({
  routes
});

router.beforeEach(async function (to, from, next) {
  if (to.matched.some(record => record.meta.requiresAuth === true)) {
    Auth.currentAuthenticatedUser().then(() => {
      next();
    }).catch(() => {
      next({ name: 'SignIn' });
    });
  } else if (to.matched.some(record => record.meta.requiresBusiness === true)) {
    Auth.currentAuthenticatedUser().then(async () => {
      if (await authHelpers.isCurrentUserBusiness() || await authHelpers.isCurrentUserAdmin()) {
        next();
      } else {
        next({ name: 'User' });
      }
    }).catch(() => {
      next({ name: 'SignIn' });
    });
  } else if (to.matched.some(record => record.meta.requiresAdmin === true)) {
    Auth.currentAuthenticatedUser().then(async () => {
      if (await authHelpers.isCurrentUserAdmin()) {
        next();
      } else {
        next({ name: 'User' });
      }
    }).catch(() => {
      next({ name: 'SignIn' });
    });
  } else {
    next();
  }
});

export default router;
